import { useState } from 'react'
import styles from './ReplayErrorModal.module.css'
import {
  REPLAY_ERRORS, REPLAY_ERROR_ACTIONS,
  ReplayErrorId, ReplayErrorActionKind, ReplayErrorGlyph,
} from './replayErrors'

// ── Fallback glyphs ──────────────────────────────────────────────────────
// Drawn in our gold line-art until Alexey's pixel-art render is fetched into
// error_illustrations/. A placeholder PNG (1×1) reports naturalWidth ≤ 4, which
// flips the hero to this glyph; a real render (large) covers it automatically.
const g = (children: React.ReactNode) => (
  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
)
const GLYPHS: Record<ReplayErrorGlyph, React.ReactNode> = {
  folder: g(<><path d="M6 14h12l4 4h20v22H6z" /><path d="M30 27l8 8M38 27l-8 8" /></>),
  plug: g(<><path d="M6 24h14M28 24h4l10-6v18l-10-6h-4" /><path d="M20 18v12M24 16v16" /><path d="M24 33l3 4" /></>),
  sign: g(<><path d="M24 30v12" /><rect x="10" y="10" width="28" height="18" /><path d="M17 17h14M17 21h9" /></>),
  empty: g(<><rect x="9" y="11" width="30" height="26" strokeDasharray="3 4" /><path d="M19 24h10" /></>),
  texture: g(<><rect x="10" y="10" width="28" height="28" /><path d="M24 10v28M10 24h28" /><path d="M10 10h14v14M24 24h14v14" fill="currentColor" fillOpacity="0.16" stroke="none" /></>),
  download: g(<><path d="M14 30a8 8 0 01-1-15.9A11 11 0 0135 17a7 7 0 011 13.9" /><path d="M24 20v13M19 28l5 5 5-5" /></>),
  playback: g(<><circle cx="24" cy="24" r="15" /><path d="M19 16l13 8-13 8z" /><path d="M11 11l26 26" /></>),
  render: g(<><rect x="7" y="11" width="34" height="22" /><path d="M19 39h10M24 33v6" /><path d="M15 19l6 6-6 6M27 19l6 6-6 6" /></>),
  lock: g(<><rect x="13" y="22" width="22" height="15" /><path d="M18 22v-4a6 6 0 0111-3.3" /><path d="M24 28v3" /></>),
  unknown: g(<><rect x="9" y="9" width="30" height="30" rx="3" /><path d="M19 20a5 5 0 119 3c-1.6 1.3-3 2-3 4M24 33v.2" /></>),
}

/* Tabler Icons (refresh / copy) — the HUD's utility-icon voice: 1.7px
   rounded strokes, same as the top command bar and the ±10s transport. */
const RefreshIco = () => (
  <svg
    className={styles.btnIco} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
    <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
  </svg>
)
const CopyIco = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" />
    <path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" />
  </svg>
)

const interpolate = (template: string, vars?: Record<string, string>) => (vars ? template.replaceAll(/{(\w+)}/g, (_, k) => vars[k] ?? `{${k}}`) : template)

export interface ReplayErrorModalProps {
  errorId: ReplayErrorId
  onAction: (kind: ReplayErrorActionKind) => void
  /** values for {version}/{maxVersion} placeholders in the description */
  vars?: Record<string, string>
  /** diagnostic code shown in the copy chip (defaults to a synthesized one) */
  code?: string
  /** preview-only toolbar rendered above the panel */
  previewSlot?: React.ReactNode
}

export default function ReplayErrorModal ({ errorId, onAction, vars, code, previewSlot }: ReplayErrorModalProps) {
  const def = REPLAY_ERRORS[errorId]
  const [hasArt, setHasArt] = useState(false)
  const [copied, setCopied] = useState(false)

  const shownCode = def.codePrefix ? (code ?? `${def.codePrefix}-XXXXXX-XXXXXX`) : undefined

  const copyCode = () => {
    if (!shownCode) return
    void navigator.clipboard?.writeText(shownCode).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1300)
    }, () => {})
  }

  return (
    <div className={styles.overlay} role="alertdialog" aria-modal="true" aria-label={def.title}>
      {previewSlot}
      <div className={styles.panel} data-tone={def.tone} data-error={errorId}>
        <div className={styles.surface}>
          <header className={styles.reportHead}>
            <span className={styles.reportIdentity}>
              <span className={styles.reportMark} aria-hidden="true" />
              <span className={styles.reportTitle}>Системный отчёт</span>
            </span>
            <span className={styles.reportLabel}>{def.reportLabel}</span>
          </header>

          <div className={styles.hero}>
            <span className={`${styles.glyph} ${hasArt ? styles.glyphHidden : ''}`} aria-hidden="true">
              {GLYPHS[def.glyph]}
            </span>
            <img
              className={styles.heroImg}
              src={def.illustration}
              alt=""
              draggable={false}
              style={{ opacity: hasArt ? 1 : 0 }}
              onLoad={e => setHasArt(e.currentTarget.naturalWidth > 4)}
            />
          </div>

          <div className={styles.titleRow}>
            <h2 className={styles.title}>{def.title}</h2>
          </div>
          <p className={styles.desc}>{interpolate(def.description, vars)}</p>

          {shownCode && (
            <button type="button" className={styles.codeChip} onClick={copyCode} title="Скопировать код ошибки">
              <span className={styles.codeText}>{shownCode}</span>
              <CopyIco />
              {copied && <span className={styles.codeCopied}>скопировано</span>}
            </button>
          )}

          <div className={styles.actions}>
            {def.actions.map(kind => {
              const a = REPLAY_ERROR_ACTIONS[kind]
              return (
                <button
                  key={kind}
                  type="button"
                  className={`${styles.btn} ${a.primary ? styles.btnPrimary : styles.btnMinor}`}
                  onClick={() => onAction(kind)}
                >
                  {a.refreshIcon && <RefreshIco />}
                  <span className={styles.btnLabel}>{a.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
